/** biome-ignore-all lint/suspicious/noExplicitAny: later fix */

import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "../../lib/utils"
import { Icon } from "../Icon/Icon"

// Container styles for circular hover effect
const containerStyles = cva(
  "flex items-center justify-center p-2 rounded-100 transition-colors " +
  // Base unchecked states (lower specificity)
  "hover:bg-cx-checkbox-base-clickarea-hover " +
  "active:bg-cx-checkbox-base-clickarea-active",
  {
    variants: {
      state: {
        default: [],
        error: [
          // Override base unchecked with error colors
          "hover:bg-cx-checkbox-danger-clickarea-hover",
          "active:bg-cx-checkbox-danger-clickarea-active",
        ],
      },
      variant: {
        labeled: [
          // Checked/indeterminate states use orange hover colors
          "has-[:checked]:hover:bg-cx-checkbox-checked-clickarea-hover",
          "has-[:checked]:active:bg-cx-checkbox-checked-clickarea-active",
          "has-[:indeterminate]:hover:bg-cx-checkbox-checked-clickarea-hover",
          "has-[:indeterminate]:active:bg-cx-checkbox-checked-clickarea-active"
        ],
        unlabeled: [
          // Checked/indeterminate states use subtle blue hover colors
          "has-[:checked]:hover:bg-blue-1-300",
          "has-[:checked]:active:bg-blue-1-400",
          "has-[:indeterminate]:hover:bg-blue-1-300",
          "has-[:indeterminate]:active:bg-blue-1-400"
        ],
      },
    },
    defaultVariants: {
      state: "default",
      variant: "labeled",
    },
  }
)

// Checkbox input styles
const checkboxStyles = cva(
  "size-4 shrink-0 rounded-4 border transition-colors appearance-none peer cursor-pointer " +
  "focus-visible:outline-none focus-visible:ring-0 " +
  "bg-cx-checkbox-base-background-default " +
  "border-cx-checkbox-base-border-default",
  {
    variants: {
      state: {
        default: ["disabled:cursor-not-allowed disabled:opacity-50"],
        error: [
          "border-cx-checkbox-danger-border-default",
          "disabled:cursor-not-allowed disabled:opacity-50",
        ],
      },
      variant: {
        labeled: [
          "checked:bg-cx-checkbox-checked-background-default",
          "checked:border-cx-checkbox-checked-background-default",
          "indeterminate:bg-cx-checkbox-checked-background-default",
          "indeterminate:border-cx-checkbox-checked-background-default"
        ],
        unlabeled: [
          "checked:bg-blue-2-500",
          "checked:border-blue-2-500",
          "indeterminate:bg-blue-2-500",
          "indeterminate:border-blue-2-500"
        ],
      },
    },
    defaultVariants: {
      state: "default",
      variant: "labeled",
    },
  }
)

export type CheckboxProps = VariantProps<typeof checkboxStyles> & {
  /**
   * Position of the label relative to the box. Ignored if no `label` is provided.
   * @default "right"
   */
  labelPosition?: "left" | "right"

  /**
   * If true (and `label` is present), stretches the checkbox+label to fill the full width
   * of its container, spacing them to the edges.
   * @default false
   */
  fullWidth?: boolean

  /** Additional classes to apply to the outer wrapper */
  className?: string

  /** Whether the checkbox is in indeterminate state */
  indeterminate?: boolean

  /** Native HTML-style onChange handler */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void

  /** Whether the checkbox is checked */
  checked?: boolean

  /** Whether the checkbox is disabled */
  disabled?: boolean
} & (
    | {
      /** ARIA label for accessibility */
      "aria-label"?: string
    }
    | {
      "aria-labelledby"?: string
    }
    | {
      label: string
    }
  ) & Omit<React.InputHTMLAttributes<HTMLInputElement>, "aria-label" | "aria-labelledby" | "label" | "variant" | "checked" | "onChange" | "disabled" | "indeterminate" | "className">

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      state = "default",
      labelPosition = "right",
      fullWidth = false,
      disabled,
      checked,
      onChange,
      indeterminate = false,
      ...props
    },
    ref
  ) => {
    const id = React.useId()
    const label = (props as { label?: string }).label

    // Determine variant based on whether label exists
    const variant = label ? "labeled" : "unlabeled"

    // Combine refs and set indeterminate
    const combinedRef = React.useCallback(
      (element: HTMLInputElement) => {
        if (element) {
          element.indeterminate = indeterminate
        }
        if (typeof ref === "function") {
          ref(element)
        } else if (ref) {
          ref.current = element
        }
      },
      [ref, indeterminate]
    )

    // Label classes
    const labelClasses = cn(
      "flex items-center gap-2 cursor-pointer select-none",
      fullWidth && "w-full justify-between",
      disabled && "opacity-50 cursor-not-allowed",
      "text-root-size-base font-root-text-weight-base text-root-text-color-base leading-root-line-height-base",
      state === "error" && "text-control-text-color-error"
    )

    const { label: _, variant: __, ...rest } = props as any;
    const checkboxContent = (
      <div className={containerStyles({ state, variant })}>
        <div className="relative size-4">
          <input
            ref={combinedRef}
            type="checkbox"
            data-state={indeterminate ? "indeterminate" : checked ? "checked" : "unchecked"}
            id={id}
            disabled={disabled}
            checked={checked}
            className={cn(checkboxStyles({ state, variant }), className)}
            onChange={onChange}
            {...rest}
          />
          <Icon
            name="Checkmark"
            className="absolute inset-p5 size-3 text-neutral-0 pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity"
          />
          <Icon
            name="Minus"
            className="absolute inset-p5 size-3 text-neutral-0 pointer-events-none opacity-0 peer-indeterminate:opacity-100 transition-opacity"
          />
        </div>
      </div>
    )

    if (!label) {
      return (
        <label className={labelClasses}>
          <span className="sr-only">{(props as any)["aria-label"]}</span>
          {checkboxContent}
        </label>
      )
    }

    return (
      <label htmlFor={id} className={labelClasses}>
        {labelPosition === "left" ? (
          <>
            <span>{label}</span>
            {checkboxContent}
          </>
        ) : (
          <>
            {checkboxContent}
            <span>{label}</span>
          </>
        )}
      </label>
    )
  }
)

Checkbox.displayName = "Checkbox"

export { Checkbox }
